import "@/App.css";
import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Splash } from "@/components/Splash";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/lib/i18n";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import ForcePassword from "@/pages/ForcePassword";
import SetupPin from "@/pages/SetupPin";
import StaffSelector from "@/pages/StaffSelector";
import PaymentResult from "@/pages/PaymentResult";
import AppShell from "@/pages/AppShell";
import Scanner from "@/pages/Scanner";
import Dashboard from "@/pages/Dashboard";
import { AdminGate } from "@/components/AdminLayout";
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const CashCarry = lazy(() => import("@/pages/CashCarry"));
const Orders = lazy(() => import("@/pages/Orders"));
const Promotions = lazy(() => import("@/pages/Promotions"));
const SupplierSetup = lazy(() => import("@/pages/SupplierSetup"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const Settings = lazy(() => import("@/pages/Settings"));
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const AdminStore = lazy(() => import("@/pages/AdminStore"));
const Offers = lazy(() => import("@/pages/Offers"));
const Team = lazy(() => import("@/pages/Team"));
const History = lazy(() => import("@/pages/History"));
const ProductInfo = lazy(() => import("@/pages/ProductInfo"));
const InventoryInsights = lazy(() => import("@/pages/InventoryInsights"));
const SupplierCart = lazy(() => import("@/pages/SupplierCart"));
const DepotItem = lazy(() => import("@/pages/DepotItem"));
const OrderDetail = lazy(() => import("@/pages/OrderDetail"));
const TeamMember = lazy(() => import("@/pages/TeamMember"));
const SupplierView = lazy(() => import("@/pages/SupplierView"));
const SettingsDetail = lazy(() => import("@/pages/SettingsDetail"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
const PhotoEntry = lazy(() => import("@/pages/PhotoEntry"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const SupplierAdd = lazy(() => import("@/pages/SupplierAdd"));
const OffersView = lazy(() => import("@/pages/OffersView"));

const PageLoader = () => <div data-testid="page-loader" className="space-y-3 pt-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-2xl bg-slate-100 animate-pulse" />)}</div>;

// Remember a deep link (e.g. an emailed promo link) so it survives the login / staff PIN redirects.
const KeepLink = ({ to }) => {
  const loc = useLocation();
  if (loc.search && !sessionStorage.getItem("zb_logged_out")) sessionStorage.setItem("zb_return_to", loc.pathname + loc.search);
  return <Navigate to={to} replace />;
};

const Gate = ({ children }) => {
  const { master, user, activeStaff } = useAuth();
  if (!master) return <KeepLink to="/auth" />;
  if (master.must_change_password) return <Navigate to="/force-password" replace />;
  if (master.needs_pin_setup) return <Navigate to="/setup-pin" replace />;
  if (!user) return <KeepLink to="/staff" />;
  if (!activeStaff) return <KeepLink to="/staff" />;
  return children;
};
const MasterOnly = ({ children }) => {
  const { master } = useAuth();
  if (!master) return <Navigate to="/auth" replace />;
  return children;
};

function App() {
  const [splash, setSplash] = useState(() => !window.location.pathname.startsWith("/admin"));
  useEffect(() => { const t = setTimeout(() => setSplash(false), 900); return () => clearTimeout(t); }, []);
  return (
    <div className="App">
      <AnimatePresence>{splash && <Splash />}</AnimatePresence>
      <AuthProvider>
        <LanguageProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/force-password" element={<MasterOnly><ForcePassword /></MasterOnly>} />
            <Route path="/setup-pin" element={<MasterOnly><SetupPin /></MasterOnly>} />
            <Route path="/staff" element={<MasterOnly><StaffSelector /></MasterOnly>} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route element={<Gate><AppShell /></Gate>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/scanner" element={<Scanner />} />
              <Route path="/photo-entry/new" element={<PhotoEntry />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/suppliers" element={<Suppliers />} />
              <Route path="/cart/:group" element={<SupplierCart />} />
              <Route path="/cashcarry" element={<CashCarry />} />
              <Route path="/depot/add-item" element={<PhotoEntry />} />
              <Route path="/depot/item/:id" element={<DepotItem />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/promotions" element={<Promotions />} />
              <Route path="/supplier-setup" element={<SupplierSetup />} />
              <Route path="/account/supplier-credentials" element={<SupplierSetup />} />
              <Route path="/activity" element={<ActivityLog />} />
              <Route path="/offers" element={<Offers />} />
              <Route path="/team" element={<Team />} />
              <Route path="/team/add-member" element={<TeamMember />} />
              <Route path="/history" element={<History />} />
              <Route path="/history/:id" element={<OrderDetail />} />
              <Route path="/product/:id" element={<ProductInfo />} />
              <Route path="/inventory-insights" element={<InventoryInsights />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/:section" element={<SettingsDetail />} />
              <Route path="/supplier/:id" element={<SupplierView />} />
              <Route path="/supplier-setup/add" element={<SupplierAdd />} />
              <Route path="/offers/view/:id" element={<OffersView />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/referral" element={<ReferralPage />} />
              <Route path="/about" element={<AboutPage />} />
            </Route>
            <Route element={<AdminGate />}>
              <Route path="/admin" element={<SuperAdmin />} />
              <Route path="/admin/store/:id" element={<AdminStore />} />
              <Route path="/admin/:section" element={<SuperAdmin />} />
              <Route path="/admin-portal" element={<SuperAdmin />} />
              <Route path="/admin/*" element={<Navigate to="/admin/stores" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/auth" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-center" richColors offset="80px" duration={2500} />
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}
export default App;
